import { useState } from "react";
import axios from "axios";
import { Paperclip, Loader2, X, Flag } from "lucide-react";
import { uploadFile } from "./LevelUpRequest";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = "w-full bg-black border border-white/15 px-3 py-2.5 text-sm text-white focus:border-[#D4AF37] outline-none";
const EMPTY = { subject_type: "company", subject_name: "", subject_ico: "", subject_email: "", category: "unpaid", description: "", truth_confirmed: false, disclaimer_ack: false };

export default function BlacklistReportForm({ auth, T }) {
  const F = T.f;
  const [open, setOpen] = useState(true);
  const [f, setF] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const add = async (file) => {
    if (!file) return;
    setBusy("file"); setMsg("");
    try { const r = await uploadFile(file, auth); setFiles((x) => [...x, r]); } catch { setMsg(F.upFail); } finally { setBusy(""); }
  };
  const submit = async () => {
    setBusy("send"); setMsg("");
    try {
      await axios.post(`${API}/blacklist`, { ...f, file_ids: files.map((x) => x.id) }, auth());
      setF(EMPTY); setFiles([]); setMsg(F.ok);
    } catch (e) { setMsg(e?.response?.status === 429 ? F.rate : F.err); } finally { setBusy(""); }
  };
  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid="blacklist-report-open" className="mt-10 inline-flex items-center gap-2 border border-red-400/60 text-red-300 px-5 py-2.5 text-sm hover:bg-red-500/10">
      <Flag size={15} /> {F.open}
    </button>
  );
  const ok = f.subject_name.trim().length >= 2 && f.description.trim().length >= 30 && files.length && f.truth_confirmed && f.disclaimer_ack;
  return (
    <div className="mt-10 border border-white/10 bg-[#0a0a0a] p-6 space-y-4" data-testid="blacklist-report-form">
      <h3 className="font-display text-2xl">{F.title}</h3>
      <p className="text-white/45 text-sm">{F.note}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <select value={f.subject_type} onChange={set("subject_type")} className={inp} data-testid="bl-type"><option value="company">{F.company}</option><option value="individual">{F.individual}</option></select>
        <select value={f.category} onChange={set("category")} className={inp} data-testid="bl-category">{Object.entries(T.cats).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <input value={f.subject_name} onChange={set("subject_name")} placeholder={F.name} className={inp} data-testid="bl-name" />
        <input value={f.subject_ico} onChange={set("subject_ico")} placeholder={F.ico} className={inp} data-testid="bl-ico" />
        <input value={f.subject_email} onChange={set("subject_email")} placeholder={F.email} className={`${inp} sm:col-span-2`} data-testid="bl-email" />
      </div>
      <textarea value={f.description} onChange={set("description")} rows={5} placeholder={F.desc} className={inp} data-testid="bl-description" />
      <div>
        <p className="text-xs text-white/50 mb-2">{F.ev}</p>
        {files.map((x) => <p key={x.id} className="text-sm text-white/70 flex items-center gap-2"><Paperclip size={13} /> {x.original_filename}
          <button onClick={() => setFiles((a) => a.filter((y) => y.id !== x.id))} className="text-white/40 hover:text-white" data-testid={`bl-file-remove-${x.id}`}><X size={13} /></button></p>)}
        <label className="mt-2 inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm text-white/70 hover:border-[#D4AF37]/60 cursor-pointer">
          {busy === "file" ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />} {F.attach}
          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => { add(e.target.files[0]); e.target.value = ""; }} data-testid="bl-file-input" />
        </label>
      </div>
      <p className="text-amber-200/80 text-xs border-l-2 border-amber-300/50 pl-3" data-testid="bl-form-disclaimer">{T.disclaimer}</p>
      <label className="flex items-start gap-2 text-sm text-white/70">
        <input type="checkbox" checked={f.disclaimer_ack} onChange={set("disclaimer_ack")} className="mt-1" data-testid="bl-disclaimer-ack" />
        {T.ack}
      </label>
      <label className="flex items-start gap-2 text-sm text-white/70">
        <input type="checkbox" checked={f.truth_confirmed} onChange={set("truth_confirmed")} className="mt-1" data-testid="bl-truth" />
        {F.truth}
      </label>
      <button onClick={submit} disabled={!ok || !!busy} data-testid="bl-submit" className="bg-[#D4AF37] text-black font-semibold px-6 py-3 text-sm disabled:opacity-40">
        {busy === "send" ? F.sending : F.send}
      </button>
      {msg && <p className="text-[#D4AF37] text-sm" data-testid="bl-msg">{msg}</p>}
    </div>
  );
}
